import React, { useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory, useRouteMatch } from 'react-router-dom'
import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ReactFlow, ReactFlowProvider, Background, Controls } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dayjs from 'dayjs'
import AdminPage from '../../../admin/shared/AdminPage'
import Loading from '../../../../molecules/Loading'
import EmptySection from '../../../../atoms/EmptySection'
import SydButton from '../../../../commonDesign/Button'
import SydTabs from '../../../../commonDesign/SydTabs'
import Icon from '../../../../atoms/Icon'
import OperationalTable from '../../../../organisms/OperationalTable'
import { useGetDag, useGetExecution, useGetExecutionLogs } from '../../../../../api/dags'
import { dagToGraph } from './util/dagToGraph'
import { autoLayout } from './util/autoLayout'
import StepNode from './nodes/StepNode'
import { withStepDefaults } from './nodes/withStepDefaults'
import WorkflowEdge from './edges/WorkflowEdge'

const nodeTypes = { step: withStepDefaults(StepNode) }
const edgeTypes = { workflow: WorkflowEdge }

const EXECUTION_TABS = {
  diagram: { label: 'Diagram', value: 'diagram' },
  logs: { label: 'Logs', value: 'logs' }
}

const useStyles = makeStyles({
  canvasContainer: {
    height: '100%',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FAFAFA'
  },
  executionLayout: {
    display: 'flex',
    height: 'calc(100vh - 240px)',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden'
  },
  canvasPane: {
    flex: 1,
    minWidth: 0
  },
  sidePanel: {
    width: 340,
    borderLeft: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    overflow: 'auto',
    flexShrink: 0
  },
  sidePanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 0',
    margin: 0
  },
  sidebarTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600
  },
  sidebarBody: {
    padding: '12px 16px 16px'
  },
  field: {
    marginBottom: 10
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#757575',
    textTransform: 'uppercase',
    marginBottom: 2
  },
  fieldValue: {
    fontSize: 13,
    wordBreak: 'break-all'
  },
  statusChip: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 4
  },
  summaryBar: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  summaryItem: {
    fontSize: 13,
    color: '#616161'
  },
  outputBlock: {
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
    padding: 8,
    fontSize: 12,
    fontFamily: 'monospace',
    maxHeight: 200,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  },
  logContext: {
    backgroundColor: '#F5F5F5',
    borderRadius: 4,
    padding: '4px 8px',
    fontSize: 11,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: 120,
    overflow: 'auto'
  }
})

const STATUS_CHIP_COLORS = {
  completed: { color: '#2E7D32', backgroundColor: '#E8F5E9' },
  failed: { color: '#C62828', backgroundColor: '#FFEBEE' },
  running: { color: '#1565C0', backgroundColor: '#E3F2FD' },
  deferred: { color: '#E65100', backgroundColor: '#FFF3E0' },
  ignored: { color: '#616161', backgroundColor: '#F5F5F5' },
  waiting: { color: '#9E9E9E', backgroundColor: '#FAFAFA' }
}

function StatusChip ({ status }) {
  const classes = useStyles()
  const chipStyle = STATUS_CHIP_COLORS[status] ?? STATUS_CHIP_COLORS.waiting
  return (
    <span className={classes.statusChip} style={chipStyle}>
      {status || 'waiting'}
    </span>
  )
}

StatusChip.propTypes = {
  status: PropTypes.string
}

function ExecutionStepSidebar ({ step, executionStep, onClose }) {
  const classes = useStyles()
  const status = executionStep?.status || 'waiting'

  return (
    <div>
      <div className={classes.sidePanelHeader}>
        <h4 className={classes.sidebarTitle}>{step.name}</h4>
        <IconButton size='small' onClick={onClose} aria-label='close'>
          <Icon name='close' customSize='1rem' />
        </IconButton>
      </div>
      <div className={classes.sidebarBody}>
        <div className={classes.field}>
          <div className={classes.fieldLabel}>Status</div>
          <StatusChip status={status} />
        </div>
        {executionStep?.startedOn ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Started</div>
            <div className={classes.fieldValue}>{dayjs(executionStep.startedOn).format('MMM D, YYYY h:mm:ss A')}</div>
          </div>
        ) : null}
        {executionStep?.finishedOn ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Finished</div>
            <div className={classes.fieldValue}>{dayjs(executionStep.finishedOn).format('MMM D, YYYY h:mm:ss A')}</div>
          </div>
        ) : null}
        <div className={classes.field}>
          <div className={classes.fieldLabel}>Action</div>
          <div className={classes.fieldValue}>{step.targetAction}</div>
        </div>
        {step.target ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Target</div>
            <div className={classes.fieldValue}>{step.target}</div>
          </div>
        ) : null}
        {step.isAsync ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Async</div>
            <div className={classes.fieldValue}>Yes</div>
          </div>
        ) : null}
        {executionStep?.output ? (
          <div className={classes.field}>
            <div className={classes.fieldLabel}>Output</div>
            <div className={classes.outputBlock}>
              {typeof executionStep.output === 'string'
                ? executionStep.output
                : JSON.stringify(executionStep.output, null, 2)}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

ExecutionStepSidebar.propTypes = {
  step: PropTypes.shape({
    name: PropTypes.string,
    targetAction: PropTypes.string,
    target: PropTypes.string,
    isAsync: PropTypes.oneOfType([PropTypes.bool, PropTypes.number])
  }),
  executionStep: PropTypes.shape({
    status: PropTypes.string,
    startedOn: PropTypes.string,
    finishedOn: PropTypes.string,
    output: PropTypes.any
  }),
  onClose: PropTypes.func
}

function ExecutionCanvas ({ dag, stepStatusMap, onNodeSelect }) {
  const classes = useStyles()

  const { nodes, edges } = useMemo(() => {
    const graph = dagToGraph(dag)
    const nodesWithStatus = graph.nodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        status: stepStatusMap[n.id]?.status || 'waiting'
      }
    }))
    const layoutNodes = autoLayout(nodesWithStatus, graph.edges)
    return { nodes: layoutNodes, edges: graph.edges }
  }, [dag, stepStatusMap])

  const handleNodeClick = useCallback((_event, node) => {
    onNodeSelect(node.data)
  }, [onNodeSelect])

  const handlePaneClick = useCallback(() => {
    onNodeSelect(null)
  }, [onNodeSelect])

  return (
    <div className={classes.canvasContainer}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.4}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  )
}

ExecutionCanvas.propTypes = {
  dag: PropTypes.object,
  stepStatusMap: PropTypes.object,
  onNodeSelect: PropTypes.func
}

const logColumns = [
  {
    id: 'timestamp',
    accessor: 'timestamp',
    Header: 'Timestamp',
    Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm:ss A') : '\u2014' // eslint-disable-line react/prop-types
  },
  { id: 'message', accessor: 'message', Header: 'Message' },
  {
    id: 'context',
    accessor: 'context',
    Header: 'Context',
    Cell: ({ value }) => { // eslint-disable-line react/prop-types
      if (!value) return '\u2014'
      const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
      return (
        <div
          style={{
            backgroundColor: '#F5F5F5',
            borderRadius: 4,
            padding: '4px 8px',
            fontSize: 11,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 120,
            overflow: 'auto'
          }}
        >
          {text}
        </div>
      )
    }
  }
]

function ExecutionLogsTab ({ executionId, isRunning }) {
  const { data: logs, isLoading } = useGetExecutionLogs(executionId, {
    refetchInterval: isRunning ? 5000 : false
  })

  if (isLoading) return <Loading />

  if (!logs?.length) {
    return (
      <EmptySection
        title='No Logs'
        description='No log entries recorded for this execution.'
      />
    )
  }

  return (
    <OperationalTable
      variant='v2'
      columns={logColumns}
      data={logs}
      defaultSort={[{ id: 'timestamp', desc: true }]}
    />
  )
}

ExecutionLogsTab.propTypes = {
  executionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRunning: PropTypes.bool
}

function ExecutionViewer () {
  const classes = useStyles()
  const { dagId, executionId, execTab } = useParams()
  const { url } = useRouteMatch()
  const history = useHistory()
  const { data: dag, isLoading: isLoadingDag } = useGetDag(dagId)
  const { data: execution, isLoading: isLoadingExec } = useGetExecution(dagId, executionId, {
    refetchInterval: (data) => (data && !data.finishDate) ? 3000 : false
  })

  const [selectedStep, setSelectedStep] = useState(null)

  const isRunning = execution && !execution.finishDate

  const stepStatusMap = useMemo(() => {
    const map = {}
    if (execution?.steps) {
      execution.steps.forEach(s => { map[s.dagStepId] = s })
    }
    return map
  }, [execution])

  const selectedExecutionStep = selectedStep
    ? stepStatusMap[selectedStep.dagStepId] ?? null
    : null

  const handleTabChange = useCallback((value) => {
    const base = url.replace(/\/(diagram|logs)$/, '')
    history.replace(`${base}/${value}`)
  }, [history, url])

  const handleClosePanel = useCallback(() => {
    setSelectedStep(null)
  }, [])

  if (isLoadingDag || isLoadingExec) {
    return <AdminPage><Loading /></AdminPage>
  }

  if (!dag || !execution) {
    return (
      <AdminPage>
        <EmptySection title='Not Found' description='Execution not found.' />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <div style={{ marginBottom: 12 }}>
        <SydButton
          size='sm'
          variant='outline'
          icon='arrow_back'
          onClick={() => history.goBack()}
        >
          Back
        </SydButton>
      </div>
      <h2 style={{ margin: '0 0 8px' }}>
        {dag.name} &mdash; Execution #{execution.executionId}
      </h2>
      <div className={classes.summaryBar} style={{ marginBottom: 12 }}>
        <span className={classes.summaryItem}>
          Status: <StatusChip status={execution.isError ? 'failed' : (execution.finishDate ? 'completed' : 'running')} />
        </span>
        {execution.startDate ? (
          <span className={classes.summaryItem}>
            Started: {dayjs(execution.startDate).format('MMM D, YYYY h:mm:ss A')}
          </span>
        ) : null}
        {execution.finishDate ? (
          <span className={classes.summaryItem}>
            Finished: {dayjs(execution.finishDate).format('MMM D, YYYY h:mm:ss A')}
          </span>
        ) : null}
      </div>

      <SydTabs.Group
        key={execTab}
        tabOptions={EXECUTION_TABS}
        defaultValue={execTab}
        onChange={handleTabChange}
      >
        <SydTabs.MiniList />
      </SydTabs.Group>

      <div style={{ marginTop: 12 }}>
        {execTab === 'diagram' ? (
          <div className={classes.executionLayout}>
            <div className={classes.canvasPane}>
              <ReactFlowProvider>
                <ExecutionCanvas
                  dag={dag}
                  stepStatusMap={stepStatusMap}
                  onNodeSelect={setSelectedStep}
                />
              </ReactFlowProvider>
            </div>
            {selectedStep ? (
              <div className={classes.sidePanel}>
                <ExecutionStepSidebar
                  step={selectedStep}
                  executionStep={selectedExecutionStep}
                  onClose={handleClosePanel}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <ExecutionLogsTab executionId={executionId} isRunning={isRunning} />
        )}
      </div>
    </AdminPage>
  )
}

export default ExecutionViewer
